import type React from 'react';

import { ruRU } from '@mui/material/locale';
import { createTheme, type Theme } from '@mui/material/styles';

import {
  palette as designPalette,
  lightPalette,
  darkPalette,
  typography as designTypography,
  shape as designShape,
  shadows as designShadows,
  borders as designBorders,
} from '@app/theme';

const headers: React.CSSProperties = {
  fontWeight: 800,
  lineHeight: 1.15,
  letterSpacing: '-0.03em',
};

const displays: React.CSSProperties = {
  fontWeight: 900,
  lineHeight: 1.05,
  letterSpacing: '-0.04em',
};

export const createAppTheme = (themeMode: 'light' | 'dark'): Theme => {
  const themePredefined = createTheme(
    {
      cssVariables: true,
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            html: {
              scrollBehavior: 'smooth',
              '@media (prefers-reduced-motion: reduce)': {
                scrollBehavior: 'auto',
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              borderRadius: designShape.buttonRadius,
              letterSpacing: '0.02em',
            },
            contained: {
              boxShadow: 'none',
              '&:hover': {
                boxShadow: designShadows.sm,
              },
              '&:active': {
                boxShadow: 'none',
              },
            },
            outlined: {
              boxShadow: 'none',
            },
            sizeLarge: ({ theme }) => ({
              fontSize: '0.8125rem',
              [theme.breakpoints.up('sm')]: {
                fontSize: '0.875rem',
              },
              [theme.breakpoints.up('md')]: {
                fontSize: '0.9375rem',
              },
              [theme.breakpoints.up('lg')]: {
                fontSize: '1.0625rem',
              },
            }),
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: theme.vars
                  ? `rgba(${theme.vars.palette.primary.mainChannel} / 0.08)`
                  : `${theme.palette.primary.main}14`,
              },
            }),
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              borderRadius: designShape.borderRadius,
              border: designBorders.default,
              boxShadow: designShadows.md,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: designShape.inputRadius,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: lightPalette.border.focus,
                borderWidth: 1,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: designPalette.primary.main,
                borderWidth: 2,
              },
              '&.Mui-focused': {
                boxShadow: designShadows.focusRing,
              },
            },
            notchedOutline: {
              borderColor: lightPalette.border.default,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            input: {
              fontSize: '0.875rem',
              padding: '12px 14px',
              '&:-webkit-autofill': {
                WebkitBoxShadow: 'unset',
                WebkitTextFillColor: 'unset',
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              fontSize: '0.8125rem',
              fontWeight: 400,
              '&.Mui-focused': {
                fontWeight: 500,
              },
            },
            asterisk: {
              color: designPalette.warning.main,
            },
          },
        },
        MuiLink: {
          defaultProps: {
            underline: 'none',
          },
          styleOverrides: {
            root: ({ theme }) => ({
              fontWeight: 500,
              fontSize: '0.875rem',
              [theme.breakpoints.down('sm')]: {
                fontSize: '0.8125rem',
              },
              '&:hover': {
                textDecoration: 'underline',
              },
            }),
          },
        },
        MuiIcon: {
          styleOverrides: {
            root: {
              boxSizing: 'content-box',
              padding: 3,
              fontSize: '1.125rem',
            },
          },
        },
        MuiTypography: {
          defaultProps: {
            variantMapping: {
              small: 'small',
              strong: 'strong',
              sub: 'sub',
              sup: 'sup',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            rounded: {
              borderRadius: designShape.borderRadius,
            },
            elevation0: {
              border: designBorders.default,
              boxShadow: 'none',
            },
            elevation1: {
              border: designBorders.default,
              boxShadow: designShadows.xs,
            },
            elevation2: {
              border: designBorders.default,
              boxShadow: designShadows.sm,
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            standard: {
              borderRadius: designShape.borderRadius,
              borderLeft: '4px solid',
            },
          },
          variants: [
            {
              props: { variant: 'standard', severity: 'error' },
              style: ({ theme }) => ({
                backgroundColor:
                  theme.palette.mode === 'light'
                    ? designPalette.error.light
                    : `rgba(211, 47, 47, 0.12)`,
                borderLeftColor: designPalette.error.main,
              }),
            },
            {
              props: { variant: 'standard', severity: 'success' },
              style: ({ theme }) => ({
                backgroundColor:
                  theme.palette.mode === 'light'
                    ? designPalette.success.light
                    : `rgba(46, 125, 50, 0.12)`,
                borderLeftColor: designPalette.success.main,
              }),
            },
            {
              props: { variant: 'standard', severity: 'warning' },
              style: ({ theme }) => ({
                backgroundColor:
                  theme.palette.mode === 'light'
                    ? designPalette.warning.light
                    : `rgba(237, 108, 2, 0.12)`,
                borderLeftColor: designPalette.warning.main,
              }),
            },
          ],
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: designShape.borderRadius,
              border: designBorders.default,
              boxShadow: designShadows.md,
            },
          },
        },
      },
      palette: {
        /**
         * Отход от дефолта MUI, и осознанный (AF-44).
         *
         * `contrastThreshold` решает, каким будет `contrastText`: MUI берёт
         * светлый текст, если его контраст с фоном >= порога, иначе тёмный.
         * Дефолт — **3**, а порог WCAG AA — **4.5**. То есть механизм «как
         * принято» выбирает текст, который доступность не проходит.
         *
         * Это не гипотеза, это наш собственный случай: пока `warning.main`
         * был `#E65100`, при пороге 3 механизм выбрал бы белый — 3.79:1.
         * При 4.5 он выбирает `rgba(0, 0, 0, 0.87)` — 5.03:1, проходит.
         *
         * И предупреждения бы не было: MUI ругается в dev только когда
         * контраст ниже **3** (`createPalette.js`, порог захардкожен), а
         * там было 3.79. Инструмент имел механизм, имел проверку — и
         * проверка молчала на настоящем нарушении.
         *
         * Сегодня порог не меняет ничего: все шесть семейств остаются с
         * белым текстом при обоих значениях. Он нужен на будущее — чтобы
         * при следующей смене цвета текст поехал за ним, а не отстал молча.
         */
        contrastThreshold: 4.5,
        mode: themeMode,
        primary: designPalette.primary,
        secondary: { main: designPalette.secondary.main },
        success: designPalette.success,
        error: designPalette.error,
        warning: designPalette.warning,
        ...(themeMode === 'light'
          ? {
              background: lightPalette.background,
              /**
               * Токены `text` передаются объектом целиком, а не по одному
               * ключу (AF-575).
               *
               * Поштучный список был здесь до 27.09.2026 и терял `muted`:
               * токен объявлен в `theme/tokens.ts`, спроецирован в
               * `lightPalette`, но до палитры MUI не доезжал. `color:
               * 'text.muted'` не резолвился, MUI отдавал строку как есть, CSS
               * её отбрасывал, и элемент наследовал цвет от родителя.
               *
               * Отказ молчаливый: ни предупреждения, ни падения сборки. На
               * логине разделитель `bgcolor: 'text.muted'` считался с
               * `backgroundColor: rgba(0, 0, 0, 0)` — прозрачным, то есть
               * невидимым. Всего таких мест было 40.
               *
               * Дописать `muted` четвёртой строкой мало: поштучный список и
               * есть дефект, следующий токен потерялся бы так же и так же
               * молча.
               */
              text: lightPalette.text,
              divider: lightPalette.divider,
            }
          : {
              divider: darkPalette.divider,
              /**
               * Тёмная ветка переопределяет ТОЛЬКО `muted` (AF-575).
               *
               * `primary`/`secondary`/`disabled` в тёмной теме выводит сам
               * MUI, и трогать их незачем. `muted` — наш токен, MUI о нём не
               * знает, поэтому без этой строки он оставался `undefined` и
               * `color: 'text.muted'` не резолвился. Значение и расчёт
               * контраста — в `theme/tokens.ts`, рядом с самим токеном.
               */
              text: darkPalette.text,
            }),
      },
      typography: {
        fontFamily: designTypography.fontFamily,
        fontSize: designTypography.fontSize,
        strong: {
          fontWeight: 'bolder',
        },
        small: {
          fontSize: '0.875rem',
        },
        sub: {
          position: 'relative',
          fontSize: '0.75rem',
          lineHeight: 0,
          verticalAlign: 'baseline',
          bottom: '-0.25rem',
        },
        sup: {
          position: 'relative',
          fontSize: '0.75rem',
          lineHeight: 0,
          verticalAlign: 'baseline',
          top: '-0.5rem',
        },
      },
    },
    ruRU,
  );

  themePredefined.typography.h1 = {
    ...headers,
    fontSize: 'calc(1.375rem + 1.5vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '2.5rem',
    },
  };

  themePredefined.typography.h2 = {
    ...headers,
    fontSize: 'calc(1.325rem + 0.9vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '2rem',
    },
  };

  themePredefined.typography.h3 = {
    ...headers,
    fontSize: 'calc(1.3rem + 0.6vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '1.75rem',
    },
  };

  themePredefined.typography.h4 = {
    ...headers,
    fontSize: 'calc(1.275rem + 0.3vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '1.5rem',
    },
  };

  themePredefined.typography.h5 = {
    ...headers,
    fontSize: 'calc(1.25rem + 0.1vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '1.25rem',
    },
  };

  themePredefined.typography.h6 = {
    ...headers,
    fontSize: 'calc(1rem + 0.05vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '1rem',
    },
  };

  themePredefined.typography.body1 = {
    fontSize: '0.875rem',
  };

  themePredefined.typography.body2 = {
    fontSize: '0.8125rem',
  };

  themePredefined.typography.display1 = {
    ...displays,
    fontSize: 'calc(1.625rem + 4.5vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '5rem',
    },
  };

  themePredefined.typography.display2 = {
    ...displays,
    fontSize: 'calc(1.575rem + 3.9vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '4.5rem',
    },
  };

  themePredefined.typography.display3 = {
    ...displays,
    fontSize: 'calc(1.525rem + 3.3vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '4rem',
    },
  };

  themePredefined.typography.display4 = {
    ...displays,
    fontSize: 'calc(1.475rem + 2.7vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '3.5rem',
    },
  };

  themePredefined.typography.display5 = {
    ...displays,
    fontSize: 'calc(1.425rem + 2.1vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '3rem',
    },
  };

  themePredefined.typography.display6 = {
    ...displays,
    fontSize: 'calc(1.375rem + 1.5vw)',
    [themePredefined.breakpoints.up('lg')]: {
      fontSize: '2.5rem',
    },
  };

  return themePredefined;
};
